"use client";

import { motion } from 'framer-motion';
import { useMemo, useState, type FormEvent } from 'react';
import Link from 'next/link';
import { useToast } from '../components/Toast';
import { useSocialLinks, findSocialLink } from '@/hooks/useSocialLinks';
import { formatSocialHref, formatSocialDisplayText } from '@/utils/socialLink';

const API = '/api';

const FALLBACK_EMAIL = 'lindywhite948@gmail.com';
const FALLBACK_PHONE_DISPLAY = '';
const FALLBACK_PHONE_TEL = '';
const FALLBACK_WHATSAPP_NUMBER = '';

const FALLBACK_FOLLOW_LINKS = [
  {
    key: 'facebook',
    label: 'Facebook',
    href: 'https://facebook.com/61586771054665',
    bg: 'bg-[#1877F2]/10',
    hover: 'hover:bg-[#1877F2]',
    iconColor: 'text-[#1877F2]',
    iconClass: 'ri-facebook-fill',
    title: 'Follow us on Facebook',
    ariaLabel: 'Follow us on Facebook',
    rawUrl: 'https://www.facebook.com/61586771054665'
  },
  {
    key: 'instagram',
    label: 'Instagram',
    href: 'https://instagram.com/lindybalinesecats',
    bg: 'bg-[#E1306C]/10',
    hover: 'hover:bg-[#E1306C]',
    iconColor: 'text-[#E1306C]',
    iconClass: 'ri-instagram-line',
    title: 'Follow us on Instagram',
    ariaLabel: 'Follow us on Instagram',
    rawUrl: 'https://www.instagram.com/lindybalinesecats/'
  },
  {
    key: 'pinterest',
    label: 'Pinterest',
    href: 'https://pinterest.com/lindybalinesecats',
    bg: 'bg-[#BD081C]/10',
    hover: 'hover:bg-[#BD081C]',
    iconColor: 'text-[#BD081C]',
    iconClass: 'ri-pinterest-line',
    title: 'Follow us on Pinterest',
    ariaLabel: 'Follow us on Pinterest',
    rawUrl: 'https://www.pinterest.com/lindybalinesecats/'
  }
];

const SOCIAL_STYLE_MAP: Record<string, { bg: string; hover: string; iconColor: string; iconClass?: string }> = {
  FACEBOOK: { bg: 'bg-[#1877F2]/10', hover: 'hover:bg-[#1877F2]', iconColor: 'text-[#1877F2]', iconClass: 'ri-facebook-fill' },
  FACEBOOK_PAGE: { bg: 'bg-[#1877F2]/10', hover: 'hover:bg-[#1877F2]', iconColor: 'text-[#1877F2]', iconClass: 'ri-pages-line' },
  FACEBOOK_GROUP: { bg: 'bg-[#1877F2]/10', hover: 'hover:bg-[#1877F2]', iconColor: 'text-[#1877F2]', iconClass: 'ri-group-line' },
  INSTAGRAM: { bg: 'bg-[#E1306C]/10', hover: 'hover:bg-[#E1306C]', iconColor: 'text-[#E1306C]', iconClass: 'ri-instagram-line' },
  TWITTER: { bg: 'bg-[#000]/10', hover: 'hover:bg-[#000]', iconColor: 'text-[#000]', iconClass: 'ri-twitter-x-line' },
  YOUTUBE: { bg: 'bg-[#FF0000]/10', hover: 'hover:bg-[#FF0000]', iconColor: 'text-[#FF0000]', iconClass: 'ri-youtube-fill' },
  TIKTOK: { bg: 'bg-[#010101]/10', hover: 'hover:bg-[#010101]', iconColor: 'text-[#010101]', iconClass: 'ri-tiktok-line' },
  PINTEREST: { bg: 'bg-[#BD081C]/10', hover: 'hover:bg-[#BD081C]', iconColor: 'text-[#BD081C]', iconClass: 'ri-pinterest-line' },
  WHATSAPP: { bg: 'bg-[#25D366]/10', hover: 'hover:bg-[#25D366]', iconColor: 'text-[#25D366]', iconClass: 'ri-whatsapp-line' },
  TELEGRAM: { bg: 'bg-[#229ED9]/10', hover: 'hover:bg-[#229ED9]', iconColor: 'text-[#229ED9]', iconClass: 'ri-telegram-line' },
  EMAIL: { bg: 'bg-[#F4A261]/10', hover: 'hover:bg-[#F4A261]', iconColor: 'text-[#F4A261]', iconClass: 'ri-mail-line' },
  PHONE: { bg: 'bg-[#2A9D8F]/10', hover: 'hover:bg-[#2A9D8F]', iconColor: 'text-[#2A9D8F]', iconClass: 'ri-phone-line' },
  WEBSITE: { bg: 'bg-[#5E60CE]/10', hover: 'hover:bg-[#5E60CE]', iconColor: 'text-[#5E60CE]', iconClass: 'ri-global-line' },
  OTHER: { bg: 'bg-warm-brown/10', hover: 'hover:bg-warm-brown', iconColor: 'text-warm-brown', iconClass: 'ri-links-line' }
};

const DEFAULT_SOCIAL_STYLE = { bg: 'bg-warm-brown/10', hover: 'hover:bg-warm-brown', iconColor: 'text-warm-brown', iconClass: 'ri-external-link-line' };
const EXCLUDED_SOCIAL_PLATFORMS = new Set(['EMAIL', 'PHONE']);

interface ContactFormValues {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
  [key: string]: string | undefined;
}

export default function ContactPage(){
  const [loading, setLoading] = useState(false);
  const { showToast, ToastComponent } = useToast();
  const { links: managedLinks } = useSocialLinks();

  const contactHighlights = useMemo(
    () => [
      {
        tag: 'Concierge',
        title: 'Concierge Consultations',
        detail: 'Share your household story with our team and receive a bespoke pairing roadmap within 48 hours.',
        gradient: 'from-rose-100/70 via-white to-white',
        icon: '🤝'
      },
      {
        tag: 'Maison',
        title: 'Immersive Salons',
        detail: 'Book virtual ateliers or intimate in-person visits to meet current litters and observe their rituals.',
        gradient: 'from-sky-100/70 via-white to-white',
        icon: '🏡'
      },
      {
        tag: 'Legacy',
        title: 'Lifetime Guidance',
        detail: 'Nutrition plans, enrichment calendars, and wellness support—available every season of your journey.',
        gradient: 'from-emerald-100/70 via-white to-white',
        icon: '🌿'
      }
    ],
    []
  );

  const contactMethods = [
    {
      key: 'email',
      title: 'Email Concierge',
      detail: 'lindywhite948@gmail.com',
      href: 'mailto:lindywhite948@gmail.com?subject=Inquiry%20from%20Website&body=Hello%20Lindy%20Balinese%20Cats%2C%0A%0AI%20would%20like%20to%20inquire%20about%3A',
      caption: 'Placement updates & bespoke pairing notes',
      iconBg: 'bg-[#F4A261]/15 text-[#F4A261]',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 7l9 6 9-6" />
        </svg>
      ),
      ariaLabel: 'Email us at lindywhite948@gmail.com',
      rawUrl: 'mailto:lindywhite948@gmail.com'
    },
    {
      key: 'phone',
      title: 'Phone Support',
      detail: '',
      href: '',
      caption: 'Available by request • 9am – 7pm ET',
      iconBg: 'bg-[#2A9D8F]/15 text-[#2A9D8F]',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.3a1 1 0 01.95.68l1.2 3.6a1 1 0 01-.5 1.2l-1.7.85a11 11 0 006.1 6.1l.85-1.7a1 1 0 011.2-.5l3.6 1.2a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.82 21 3 14.18 3 6V5z" />
        </svg>
      ),
      ariaLabel: 'Call us at (606) 465-2993',
      rawUrl: ''
    },
    {
      key: 'whatsapp',
      title: 'WhatsApp Concierge',
      detail: 'Chat with us on WhatsApp',
      href: 'https://wa.me/?text=Hello%20Lindy%20Balinese%20Cats%2C%20I%20would%20like%20to%20inquire%20about%3A',
      caption: 'Instant chat for quick updates & live videos',
      iconBg: 'bg-[#25D366]/15 text-[#25D366]',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.52 3.48A11.82 11.82 0 0012 0C5.4 0 0 5.04 0 11.25a11 11 0 001.6 5.75L0 24l6.27-2.05a12.54 12.54 0 005.73 1.4h.01c6.6 0 12-5.04 12-11.25a10.86 10.86 0 00-3.49-8.62ZM12 21.07a10.48 10.48 0 01-5.19-1.38l-.37-.22-3.72 1.21 1.22-3.46-.24-.36a9.64 9.64 0 01-1.5-5.2c0-5.32 4.42-9.64 9.86-9.64a9.93 9.93 0 016.91 2.68 8.95 8.95 0 012.93 6.64c0 5.32-4.42 9.64-9.9 9.64Zm5.43-7.23c-.3-.15-1.77-.87-2.05-.97-.27-.1-.47-.15-.67.15-.2.29-.77.97-.94 1.17-.17.19-.35.22-.65.07-.3-.15-1.25-.45-2.39-1.43-.88-.74-1.48-1.65-1.65-1.93-.17-.29-.02-.44.13-.59.13-.13.3-.34.45-.51.15-.17.2-.29.3-.49.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.48-.5-.67-.51h-.57c-.2 0-.52.07-.79.37s-1.04 1.02-1.04 2.48 1.07 2.88 1.22 3.08c.15.19 2.11 3.21 5.1 4.36.71.28 1.26.45 1.69.58.71.22 1.36.19 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.31.17-1.42-.08-.11-.27-.18-.57-.32Z" />
        </svg>
      ),
      ariaLabel: 'Chat with us on WhatsApp',
      rawUrl: 'https://wa.me/'
    }
  ];

  const followLinks = FALLBACK_FOLLOW_LINKS;

  const facebookGroup = useMemo(() => {
    const link = findSocialLink(managedLinks, 'FACEBOOK_GROUP');
    return {
      href: link ? formatSocialHref(link) : 'https://www.facebook.com/groups/797522661874602',
      label: link ? formatSocialDisplayText(link, 'Join the Facebook group') : 'Join the Facebook group'
    };
  }, [managedLinks]);

  const chatLink = useMemo(() => {
    const whatsapp = findSocialLink(managedLinks, 'WHATSAPP');
    if (whatsapp) {
      return {
        href: formatSocialHref(whatsapp),
        label: formatSocialDisplayText(whatsapp, 'Chat on WhatsApp'),
        platform: 'WHATSAPP'
      };
    }

    const phone = findSocialLink(managedLinks, 'PHONE');
    if (phone) {
      return {
        href: formatSocialHref(phone),
        label: formatSocialDisplayText(phone, 'Phone'),
        platform: 'PHONE'
      };
    }

    return {
      href: FALLBACK_FOLLOW_LINKS[0].href,
      label: 'Chat with our team',
      platform: 'MESSENGER'
    };
  }, [managedLinks]);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);

    try {
      const form = e.currentTarget;
      const formData = new FormData(form);

      const collectedValues: Record<string, string> = {};
      formData.forEach((value, key) => {
        if (typeof value === 'string') {
          collectedValues[key] = value;
        }
      });

      // Type-safe extraction of form values
      const formValues: ContactFormValues = {
        name: String(collectedValues.name || '').trim(),
        email: String(collectedValues.email || '').trim(),
        phone: String(collectedValues.phone || '').trim(),
        subject: String(collectedValues.subject || 'general').trim(),
        message: String(collectedValues.message || '').trim()
      };

      // Validate required fields
      if (!formValues.name || !formValues.email || !formValues.message) {
        showToast('Please fill in all required fields', 'error');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formValues.email)) {
        showToast('Please enter a valid email address', 'error');
        return;
      }

      const normalizedSubject = formValues.subject || 'general';
      const payload = {
        name: formValues.name,
        email: formValues.email,
        phone: formValues.phone,
        subject: normalizedSubject,
        message: formValues.message,
        type: normalizedSubject
      };

      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send message');
      }

      showToast('Your message has been sent successfully! We\'ll get back to you soon.', 'success');
      form.reset();
    } catch (err) {
      console.error('Form submission error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      showToast(`Error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="min-h-screen bg-cream">
      {ToastComponent}

      <section className="relative overflow-hidden px-6 py-24">
        <motion.div
          className="pointer-events-none absolute -top-32 left-10 h-72 w-72 rounded-full bg-soft-gold/30 blur-3xl"
          animate={{ opacity: [0.25, 0.45, 0.25], scale: [0.9, 1.05, 0.9] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="pointer-events-none absolute bottom-[-10rem] right-[-8rem] h-96 w-96 rounded-full bg-rose-200/30 blur-[160px]"
          animate={{ opacity: [0.2, 0.4, 0.2], scale: [0.85, 1.08, 0.92] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
        />

        <div className="relative z-10 max-w-6xl mx-auto grid gap-10 lg:grid-cols-[1.05fr,0.95fr] items-center">
          <div className="space-y-6">
            <motion.span
              className="inline-flex items-center gap-2 rounded-full border border-soft-gold/40 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-soft-gold"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Concierge welcome
            </motion.span>
            <motion.h1
              className="font-heading text-4xl md:text-5xl font-bold text-warm-brown"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              Let’s design the perfect Balinese chapter for your home
            </motion.h1>
            <motion.p
              className="text-lg text-warm-brown/75 max-w-2xl"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.18 }}
            >
              Share your rhythm, ask your questions, and explore how our boutique program pairs luminous kittens with intentional families. We respond personally within two sunrises.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.24 }}
            >
              <Link
                href="#contact-form"
                className="inline-flex items-center justify-center rounded-xl bg-soft-gold px-6 py-3 text-sm font-semibold text-warm-brown shadow-lg transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:bg-soft-gold/80"
              >
                Start with a message
              </Link>
              <Link
                href="/process"
                className="inline-flex items-center justify-center rounded-xl border border-soft-gold px-6 py-3 text-sm font-semibold text-soft-gold transition-colors duration-300 hover:bg-soft-gold hover:text-warm-brown"
              >
                Explore the process
              </Link>
            </motion.div>
          </div>
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white/90 rounded-3xl border border-soft-gold/30 shadow-lg p-8">
              <h2 className="font-heading text-2xl font-semibold text-warm-brown mb-6">Connect directly</h2>
              <div className="space-y-5">
                {contactMethods.map((method) => (
                  <motion.a
                    key={method.key}
                    href={method.href}
                    className="flex items-start gap-4 rounded-2xl border border-soft-gold/20 bg-cream/40 px-4 py-4 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
                    whileHover={{ scale: 1.01 }}
                    aria-label={method.ariaLabel || method.title}
                    title={method.title}
                  >
                    <span className={`mt-1 flex h-10 w-10 items-center justify-center rounded-full ${method.iconBg}`}>
                      {method.icon}
                    </span>
                    <span>
                      <p className="font-semibold text-warm-brown">{method.title}</p>
                      <p className="text-sm text-warm-brown/80">{method.detail}</p>
                      <p className="text-xs uppercase tracking-[0.25em] text-soft-gold mt-2">{method.caption}</p>
                    </span>
                  </motion.a>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-soft-gold/30 bg-white/85 p-8 shadow-[0_24px_60px_rgba(196,166,108,0.25)] backdrop-blur">
              <div className="space-y-4 text-sm text-warm-brown/75">
                <p className="font-semibold text-warm-brown">Our current response cadence</p>
                <p>Concierge inbox is reviewed twice daily. Expect a warm note back—and whenever possible, a short voice memo—to begin your tailored introduction.</p>
                <div className="rounded-2xl bg-soft-gold/10 p-4">
                  <p className="text-xs uppercase tracking-[0.3em] text-soft-gold">Office hours</p>
                  <p className="text-warm-brown/75">Monday to Saturday • 9am – 7pm (Eastern Time)</p>
                </div>
                <div className="rounded-2xl bg-soft-gold/10 p-4">
                  <p className="text-xs uppercase tracking-[0.3em] text-soft-gold">Salon previews</p>
                  <p className="text-warm-brown/75">Virtual introductions every Thursday evening</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="px-6 pb-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {contactHighlights.map((item, index) => (
            <motion.article
              key={item.title}
              className={`relative overflow-hidden h-full rounded-3xl bg-gradient-to-br ${item.gradient} p-6 shadow-lg border border-white/40`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              viewport={{ once: true }}
            >
              <span className="text-xs uppercase tracking-[0.3em] text-warm-brown/70 block mb-3">{item.tag}</span>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-2xl">{item.icon}</span>
                <h3 className="font-heading text-xl text-warm-brown">{item.title}</h3>
              </div>
              <p className="text-sm text-warm-brown/70 leading-relaxed">{item.detail}</p>
            </motion.article>
          ))}
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr,0.9fr] gap-12">
          {/* Contact Form */}
          <motion.div
            id="contact-form"
            className="relative overflow-hidden rounded-3xl border border-soft-gold/30 bg-white/90 p-10 shadow-[0_24px_70px_rgba(196,166,108,0.28)]"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-soft-gold/15 via-transparent to-white"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            />
            <div className="relative z-10">
            <h2 className="font-heading text-2xl font-semibold text-warm-brown mb-6">Send us a Message</h2>
            <form onSubmit={submit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-warm-brown mb-2">Full Name *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Enter your full name"
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white transition-all duration-200"
                    required
                    minLength={2}
                    maxLength={100}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-warm-brown mb-2">Email Address *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your.email@example.com"
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white transition-all duration-200"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-warm-brown mb-2">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="(123) 456-7890"
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white transition-all duration-200"
                    pattern="[0-9\-\+\(\)\s]*"
                    maxLength={20}
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-warm-brown mb-2">Subject</label>
                  <select
                    id="subject"
                    name="subject"
                    className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white transition-all duration-200"
                    defaultValue="general"
                  >
                    <option value="general">General Inquiry</option>
                    <option value="kittens">Available Kittens</option>
                    <option value="breeding">Breeding Program</option>
                    <option value="application">Kitten Application</option>
                    <option value="visitation">Schedule a Visit</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-warm-brown mb-2">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us how we can help you..."
                  rows={6}
                  className="w-full px-4 py-3 border border-sand/30 rounded-xl focus:ring-2 focus:ring-soft-gold focus:border-transparent bg-white transition-all duration-200"
                  required
                  minLength={10}
                  maxLength={2000}
                />
              </div>

              <motion.button
                disabled={loading}
                type="submit"
                className="relative w-full py-4 bg-soft-gold text-warm-brown rounded-xl overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-lg shadow-lg hover:shadow-xl hover:bg-soft-gold/80"
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
              >
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  animate={{ opacity: [0.1, 0.35, 0.1] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
                />
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-warm-brown mr-2"></div>
                    Sending Message...
                  </div>
                ) : (
                  'Send Message'
                )}
              </motion.button>
            </form>
            </div>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="relative overflow-hidden rounded-3xl border border-[#1877F2]/25 bg-gradient-to-br from-[#1877F2]/15 via-white to-cream p-8 shadow-[0_24px_60px_rgba(24,119,242,0.18)]">
              <motion.div
                className="pointer-events-none absolute -top-20 -left-16 h-44 w-44 rounded-full bg-[#1877F2]/35 blur-3xl"
                animate={{ opacity: [0.2, 0.4, 0.2], scale: [0.85, 1.05, 0.9] }}
                transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="pointer-events-none absolute bottom-[-6rem] right-[-8rem] h-64 w-64 rounded-full bg-sky-200/40 blur-[140px]"
                animate={{ opacity: [0.18, 0.32, 0.18], scale: [0.8, 1.1, 0.9] }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 1.1 }}
              />

              <div className="relative z-10 space-y-5 text-[#0B1F4E]">
                <span className="inline-flex items-center gap-2 rounded-full border border-[#1877F2]/30 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#1877F2]">
                  Facebook Lounge
                </span>
                <h3 className="font-heading text-2xl font-semibold text-[#0B1F4E]">
                  Join our private family group
                </h3>
                <p className="text-sm text-[#0B1F4E]/80 leading-relaxed">
                  Swap enrichment ideas, watch nursery livestreams, and get first alerts when litters open to the waitlist.
                </p>
                <ul className="space-y-2 text-sm text-[#0B1F4E]/75">
                  <li className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1877F2]/15 text-[#1877F2]">✓</span>
                    Weekly behind-the-scenes drops
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1877F2]/15 text-[#1877F2]">✓</span>
                    Live Q&A with the breeding team
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#1877F2]/15 text-[#1877F2]">✓</span>
                    Early access to placement windows
                  </li>
                </ul>
                <motion.a
                  href="https://www.facebook.com/groups/797522661874602"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1877F2] px-6 py-3 text-sm font-semibold text-white shadow-lg transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  Join the Facebook group
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </motion.a>
              </div>
            </div>

            <div className="bg-white/90 rounded-3xl border border-soft-gold/30 shadow-lg p-8">
              <h2 className="font-heading text-2xl font-semibold text-warm-brown mb-6">Follow along</h2>
              <p className="text-warm-brown/70 mb-6">
                Join our digital salons for behind-the-scenes nursery stories, exclusive reels, and adoption day celebrations.
              </p>
              <div className="flex flex-wrap gap-4">
                {followLinks.map((link) => (
                  <div key={link.key} className="relative group">
                    {/* Hidden raw link for SEO - will be hidden by global styles */}
                    <a href={link.rawUrl || link.href} className="hidden">
                      {link.label}
                    </a>
                    
                    {/* Styled button with icon */}
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex h-12 items-center gap-3 rounded-full px-5 transition-all duration-300 ${link.bg} ${link.hover}`}
                      aria-label={link.ariaLabel || link.title || `Follow us on ${link.label}`}
                      title={link.title || `Follow us on ${link.label}`}
                    >
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-white/70 ${link.iconColor} group-hover:bg-white/90`}>
                        <i className={link.iconClass}></i>
                      </span>
                      <span className="text-sm font-semibold text-warm-brown group-hover:text-white">{link.label}</span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <motion.a
        href="https://m.me/61586771054665"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-4 sm:right-8 z-40"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        aria-label="Chat with us on Messenger"
        title="Chat with us on Messenger"
      >
        <motion.span
          className="relative flex items-center gap-3 rounded-full bg-[#0084FF] text-white px-4 py-3 shadow-[0_18px_40px_rgba(0,132,255,0.45)]"
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white/20">
            <i className={
              'ri-messenger-line text-xl'
            }></i>
          </span>
          <span className="hidden sm:flex flex-col text-left">
            <span className="text-xs uppercase tracking-[0.35em] text-white/70">Chat</span>
            <span className="font-heading text-base">Message Us</span>
          </span>
        </motion.span>
      </motion.a>
    </main>
  );
}
